import { useRef, useState } from 'react';
import { Copy, CheckCircle, ArrowSquareOut } from '@phosphor-icons/react';

/**
 * Email Signature Builder — copy-paste-ready HTML signature with live RNG pulse grid.
 * URL: /email-signature  (public, no auth required)
 */
const EmailSignaturePage = () => {
  const signatureRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!signatureRef.current) return;
    try {
      // Modern clipboard API with rich HTML support
      const html = signatureRef.current.innerHTML;
      const blob = new Blob([html], { type: 'text/html' });
      const text = new Blob([signatureRef.current.innerText], { type: 'text/plain' });
      await navigator.clipboard.write([
        new ClipboardItem({ 'text/html': blob, 'text/plain': text }),
      ]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      // Fallback: copy outer HTML as plain text
      const range = document.createRange();
      range.selectNode(signatureRef.current);
      window.getSelection().removeAllRanges();
      window.getSelection().addRange(range);
      document.execCommand('copy');
      window.getSelection().removeAllRanges();
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    }
  };

  const gifUrl = `${window.location.origin}/signature/pulse-grid.gif`;

  return (
    <div className="min-h-screen bg-[#0a0703] text-[#cba135] py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <h1 className="heading-font text-3xl md:text-4xl text-[#e6be4b] mb-2">
          Email Signature
        </h1>
        <p className="text-sm text-[#cba135]/60 mb-8">
          Copy and paste into Gmail / Outlook / Apple Mail. The pulse grid is a live animation
          from <code className="text-[#e6be4b]">{gifUrl}</code>.
        </p>

        {/* Preview rendered EXACTLY as it will appear in email clients */}
        <div className="bg-white rounded-lg p-8 mb-6 shadow-2xl" data-testid="signature-preview-wrap">
          <p className="text-gray-700 font-sans text-sm mb-6">
            Best regards,<br />
          </p>

          {/* ↓↓↓ Email signature begins ↓↓↓ */}
          <div ref={signatureRef} data-testid="signature-html">
            <table cellPadding="0" cellSpacing="0" border="0" style={{
              borderCollapse: 'collapse',
              backgroundColor: '#0e0b04',
              borderRadius: '6px',
              fontFamily: "Georgia, 'Times New Roman', serif",
              color: '#cba135',
              border: '1px solid rgba(203,161,53,0.18)',
            }}>
              <tbody>
                <tr>
                  <td style={{ padding: '18px 22px', verticalAlign: 'middle', borderRight: '1px solid rgba(203,161,53,0.22)' }}>
                    <div style={{
                      fontFamily: "Georgia, 'Times New Roman', serif",
                      color: '#e6be4b',
                      fontSize: '28px',
                      fontWeight: 'bold',
                      letterSpacing: '1px',
                      lineHeight: '1',
                    }}>
                      1:2000
                    </div>
                    <div style={{
                      fontFamily: "Georgia, 'Times New Roman', serif",
                      color: '#cba135',
                      fontSize: '15px',
                      letterSpacing: '7px',
                      lineHeight: '1.4',
                      marginTop: '4px',
                    }}>
                      CLUB
                    </div>
                    <div style={{
                      fontFamily: "Arial, Helvetica, sans-serif",
                      color: '#a8872b',
                      fontSize: '8px',
                      letterSpacing: '2.5px',
                      textTransform: 'uppercase',
                      marginTop: '10px',
                    }}>
                      Exclusive Lottery
                    </div>
                  </td>
                  <td style={{ padding: '18px 22px', verticalAlign: 'middle' }}>
                    <div style={{
                      fontFamily: "Georgia, 'Times New Roman', serif",
                      color: '#e6be4b',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      lineHeight: '1.2',
                    }}>
                      Stanislav Balaz
                    </div>
                    <div style={{
                      fontFamily: "Arial, Helvetica, sans-serif",
                      color: '#a8872b',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '2.5px',
                      marginTop: '3px',
                    }}>
                      Founder &amp; CEO
                    </div>
                    <div style={{
                      marginTop: '12px',
                      fontFamily: "Arial, Helvetica, sans-serif",
                      fontSize: '12px',
                      color: '#cba135',
                      lineHeight: '1.6',
                    }}>
                      <a href="tel:+421907153416" style={{ color: '#cba135', textDecoration: 'none' }}>
                        +421 907 153 416
                      </a><br />
                      <a href="https://1in2000club.com" style={{ color: '#e6be4b', textDecoration: 'none' }}>
                        1in2000club.com
                      </a>
                    </div>
                  </td>
                  <td style={{ padding: '14px 22px 14px 0', verticalAlign: 'middle' }}>
                    <img
                      src={gifUrl}
                      alt="Live RNG"
                      width="180"
                      height="98"
                      style={{ display: 'block', border: 0, outline: 'none', textDecoration: 'none' }}
                    />
                    <div style={{
                      fontFamily: "Arial, Helvetica, sans-serif",
                      color: '#a8872b',
                      fontSize: '8px',
                      letterSpacing: '3px',
                      textTransform: 'uppercase',
                      marginTop: '6px',
                      textAlign: 'center',
                    }}>
                      Randomness · Live
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {/* ↑↑↑ Email signature ends ↑↑↑ */}

          <p className="text-gray-400 text-[10px] mt-6 font-sans">
            This email and any attachments are confidential. 1:2000 Club operates a closed-pool lottery.
          </p>
        </div>

        <div className="flex flex-wrap gap-3 mb-10">
          <button
            onClick={handleCopy}
            data-testid="copy-signature-button"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-gradient-to-r from-[#cba135] to-[#a8872b] text-[#0e0b04] font-bold text-sm uppercase tracking-wider transition-transform hover:scale-[1.02]"
          >
            {copied ? <CheckCircle size={18} weight="fill" /> : <Copy size={18} weight="bold" />}
            {copied ? 'Copied!' : 'Copy signature'}
          </button>
          <a
            href={gifUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-[#cba135]/30 text-[#cba135] font-medium text-sm uppercase tracking-wider hover:bg-[#cba135]/10"
          >
            <ArrowSquareOut size={18} /> Pulse-grid.gif
          </a>
        </div>

        <SetupGuide gifUrl={gifUrl} />
      </div>
    </div>
  );
};

const SetupGuide = ({ gifUrl }) => (
  <div className="bg-[#1a1508]/80 border border-[#cba135]/15 rounded-lg p-6 space-y-6 text-sm leading-relaxed">
    <h2 className="heading-font text-2xl text-[#e6be4b]">Setup guide</h2>

    <Step n="1" title="Gmail">
      <ol className="list-decimal list-inside space-y-1 text-[#cba135]/85">
        <li>Open <span className="text-[#e6be4b]">Gmail → ⚙️ Settings → See all settings</span></li>
        <li>Tab <strong>General</strong> → scroll down to the <strong>Signature</strong> section</li>
        <li>Click <strong>+ Create new</strong>, give it a name (e.g. "1:2000 Club")</li>
        <li>Click the <span className="text-[#e6be4b]">"Copy signature"</span> button above and paste with <kbd>Ctrl+V</kbd> (Mac: <kbd>⌘+V</kbd>) into the signature field</li>
        <li>Below set <strong>Signature defaults for new emails</strong> → <em>1:2000 Club</em></li>
        <li>Click <strong>Save changes</strong> ✔</li>
      </ol>
    </Step>

    <Step n="2" title="Outlook (Web / 365)">
      <ol className="list-decimal list-inside space-y-1 text-[#cba135]/85">
        <li>Outlook web → ⚙️ <strong>Settings</strong> → <strong>Mail → Compose and reply</strong></li>
        <li>In the <strong>Email signature</strong> field click into the empty box</li>
        <li><kbd>Ctrl+V</kbd> (after clicking "Copy signature" above)</li>
        <li>Check <strong>Automatically include my signature</strong> for new emails and replies</li>
      </ol>
    </Step>

    <Step n="3" title="Apple Mail (macOS)">
      <ol className="list-decimal list-inside space-y-1 text-[#cba135]/85">
        <li><strong>Mail → Settings → Signatures</strong></li>
        <li>Select an account on the left, click <strong>+</strong> to add a signature</li>
        <li>Click into the right panel, <kbd>⌘+V</kbd> (after copying above)</li>
        <li>Uncheck "Always match my default message font" — so colors and fonts are kept</li>
      </ol>
    </Step>

    <Step n="4" title="Why is the GIF animated and when isn't it?">
      <ul className="list-disc list-inside space-y-1 text-[#cba135]/85">
        <li>The GIF is hosted at <code className="text-[#e6be4b]">{gifUrl}</code> — every time the email is opened the client downloads it and plays it in a loop</li>
        <li>✅ Plays in: Gmail web, Outlook web, Apple Mail (macOS &amp; iOS), Thunderbird, most mobile clients</li>
        <li>⚠️ Static first frame: some corporate Outlook desktop versions (2019/2021) disable animated GIFs for security reasons</li>
        <li>⚠️ Will not show at all: if the recipient has external image loading disabled</li>
      </ul>
    </Step>

    <Step n="5" title="Update / customization">
      <p className="text-[#cba135]/85">
        Want a different color, number of dots, or pulse speed? Edit{' '}
        <code className="text-[#e6be4b]">/app/scripts/generate_signature_gif.py</code>{' '}
        and run <code className="text-[#e6be4b]">python3 generate_signature_gif.py</code>.
        Then <strong>Save to Github → Redeploy</strong>.
      </p>
    </Step>
  </div>
);

const Step = ({ n, title, children }) => (
  <div>
    <div className="flex items-center gap-3 mb-2">
      <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-[#cba135]/15 border border-[#cba135]/30 text-[#e6be4b] text-xs font-bold">
        {n}
      </span>
      <h3 className="heading-font text-lg text-[#e6be4b]">{title}</h3>
    </div>
    <div className="pl-10">{children}</div>
  </div>
);

export default EmailSignaturePage;
